import type {
    PoolInventoryUploadModel,
    UploadApplyProgress,
    UploadCategory,
    UploadFileInfo,
    UploadFilteredEntries,
    UploadMode,
    UploadStreamError,
    UploadWizardStep,
} from '.';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { InventoryUploadErrorReason } from '@thinkalpha/locates-ws-client';
import type {
    AddPoolItemsResponse,
    InventoryUploadError,
    ListPoolItemsResponse,
    NewPoolItem,
} from '@thinkalpha/locates-ws-client';
import { t } from 'i18next';
import { diffInventory } from 'src/admin/models/PoolInventoryUploadModel/upload/diffInventory';
import type { UploadDiff } from 'src/admin/models/PoolInventoryUploadModel/upload/diffInventory';
import { openInventoryUploadStream } from 'src/admin/models/PoolInventoryUploadModel/upload/inventoryUploadStream';
import {
    createInventoryTemplateCsv,
    parseInventoryCsv,
} from 'src/admin/models/PoolInventoryUploadModel/upload/parseInventoryCsv';
import type { InventoryParseResult } from 'src/admin/models/PoolInventoryUploadModel/upload/parseInventoryCsv';
import { failedUploadRowCount } from 'src/admin/models/PoolInventoryUploadModel/upload/streamErrorSummary';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAllPoolItemsQuery, invalidatePoolChangeLogQuery, invalidatePoolItemsQuery } from 'src/queries/labPools';
import { addPoolItems, removePoolItem, updatePoolItem } from 'src/queries/labPoolsMutations';
import {
    getLocatesAuthPayloadFromState,
    getLocatesSocketConnectionSettingsFromState,
} from 'src/store/selectors/locates/locatesSocket';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Hard size cap so a stray multi-hundred-megabyte file can't lock up the
 * main thread inside `file.text()`. 8 MB is well above the 10k-row CSV
 * the wizard advertises (~700 KB) but small enough to parse
 * synchronously without UI jank.
 */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * How long the apply waits for the upload-item stream to go idle before
 * concluding the remaining adds won't be confirmed row-by-row.
 */
const UPLOAD_STREAM_IDLE_MS = 10_000;

/**
 * Grace window after an idle timeout for a late error event: idling out
 * short of the expected count with no claimed error is exactly the
 * shape of a persistence failure whose error event hasn't landed yet.
 */
const UPLOAD_STREAM_ERROR_GRACE_MS = 3_000;

/** How long the apply waits for the stream to authenticate and join the room. */
const UPLOAD_STREAM_READY_MS = 5_000;

/** Brief hold at 100% so the apply reads as visually complete before the done screen. */
const UPLOAD_COMPLETE_HOLD_MS = 300;

/** Everything the upload model needs from its owner (the inventory side). */
export interface PoolInventoryUploadModelDeps {
    /** The pool this upload targets; part of the instance's identity. */
    readonly poolId: string;
    readonly queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly queryClient: ReactBindings['QueryClient'];
    readonly store: ReactBindings['Store'];
    /** Write access for the pool; file selection and apply fail closed when it drops. */
    readonly canWrite: ReadonlySignal<boolean>;
    /**
     * The apply refetched the pool's server rows (also fires on a
     * partial failure, which may have landed rows) — the owner bumps
     * its freshness stamp.
     */
    onServerRowsRefetched(): void;
    /**
     * The apply succeeded: the upload supersedes any local pending adds
     * the owner staged for this pool.
     */
    onApplySucceeded(): void;
}

/**
 * See {@link PoolInventoryUploadModel}. State is plain signals — the
 * open/close/navigation reset protocol the old reducer slice needed is
 * gone because closing the wizard disposes the instance.
 */
export class PoolInventoryUploadModelImpl implements PoolInventoryUploadModel {
    readonly #disposableStack = new DisposableStack();
    readonly #deps: PoolInventoryUploadModelDeps;
    readonly #fullPoolItemsQuery: QueryObserverModel<ListPoolItemsResponse, Error, ListPoolItemsResponse>;

    readonly poolId: string;

    readonly #step = signal<UploadWizardStep>('drop');
    readonly #file = signal<UploadFileInfo | null>(null);
    readonly #parseResult = signal<InventoryParseResult | null>(null);
    readonly #categoryFilter = signal<UploadCategory>('added');
    readonly #symbolFilter = signal('');
    readonly #mode = signal<UploadMode>('merge');
    readonly #isApplying = signal(false);
    readonly #progress = signal<UploadApplyProgress | null>(null);
    readonly #streamErrors = signal<ReadonlyArray<UploadStreamError>>([]);
    readonly #error = signal<string | null>(null);
    readonly #appliedSummary = signal<{ added: number; updated: number; removed: number; rejected: number } | null>(
        null,
    );

    constructor(deps: PoolInventoryUploadModelDeps) {
        this.#deps = deps;
        this.poolId = deps.poolId;
        // The CSV diff genuinely needs every row, so the full-pool
        // snapshot is fetched for exactly this instance's lifetime.
        const options: QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse> = getAllPoolItemsQuery(
            deps.poolId,
        );
        this.#fullPoolItemsQuery = deps.queryObserverModelFactory<ListPoolItemsResponse, Error, ListPoolItemsResponse>(
            options,
        );
        this.#disposableStack.use(this.#fullPoolItemsQuery);
    }

    readonly step: ReadonlySignal<UploadWizardStep> = computed(() => this.#step.value);
    readonly file: ReadonlySignal<UploadFileInfo | null> = computed(() => this.#file.value);
    readonly parseResult: ReadonlySignal<InventoryParseResult | null> = computed(() => this.#parseResult.value);
    readonly categoryFilter: ReadonlySignal<UploadCategory> = computed(() => this.#categoryFilter.value);
    readonly symbolFilter: ReadonlySignal<string> = computed(() => this.#symbolFilter.value);
    readonly mode: ReadonlySignal<UploadMode> = computed(() => this.#mode.value);
    readonly isApplying: ReadonlySignal<boolean> = computed(() => this.#isApplying.value);
    readonly progress: ReadonlySignal<UploadApplyProgress | null> = computed(() => this.#progress.value);
    readonly streamErrors: ReadonlySignal<ReadonlyArray<UploadStreamError>> = computed(() => this.#streamErrors.value);
    readonly failedRowCount: ReadonlySignal<number> = computed(() => failedUploadRowCount(this.#streamErrors.value));
    readonly appliedSummary: ReadonlySignal<{
        added: number;
        updated: number;
        removed: number;
        rejected: number;
    } | null> = computed(() => this.#appliedSummary.value);

    readonly error: ReadonlySignal<string | null> = computed(() => {
        const stateError = this.#error.value;
        if (stateError) return stateError;
        // Surface a failed full-pool snapshot fetch: without it the diff
        // stays null exactly like the loading state and the Continue
        // gate would stay closed with no message or way forward.
        return this.#fullPoolItemsQuery.signal.value.error?.message ?? null;
    });

    readonly diff: ReadonlySignal<UploadDiff | null> = computed(() => {
        const parsed = this.#parseResult.value;
        if (!parsed) return null;
        // Diff against the full-pool snapshot — never the paged table
        // query. `null` while the snapshot loads keeps the Continue gate
        // closed so an apply can't run against a partial view of the
        // pool. Pending local adds don't count as "existing" inventory
        // from the upload's perspective.
        const data = this.#fullPoolItemsQuery.signal.value.data;
        if (!data) return null;
        return diffInventory(
            parsed.rows,
            data.items.map((item) => ({
                id: item.id,
                symbol: item.symbol,
                currentQuantity: item.quantity,
                // Cleared prices come back as `null` (backend "clear via
                // PATCH price: null" semantics); coerce to 0 at the boundary
                // exactly like the inventory table's row mapping, so a
                // cleared price diffs as 0 instead of NaN-ish comparisons.
                currentLabPrice: item.price ?? 0,
            })),
        );
    });

    readonly missingPriceAddCount: ReadonlySignal<number> = computed(
        () => (this.diff.value?.added ?? []).filter((item) => item.labPrice === null).length,
    );

    readonly canAdvance: ReadonlySignal<boolean> = computed(() => {
        const step = this.#step.value;
        if (step === 'drop') {
            const parsed = this.#parseResult.value;
            return this.#file.value !== null && parsed !== null && parsed.rows.length > 0 && this.error.value === null;
        }
        if (step === 'review') {
            // Blocking here (not just on apply) keeps the user in the
            // wizard with a fixable error instead of a rejected apply.
            return this.diff.value !== null && this.missingPriceAddCount.value === 0;
        }
        return false;
    });

    readonly availableCategories: ReadonlySignal<ReadonlyArray<UploadCategory>> = computed(() =>
        this.#mode.value === 'replace'
            ? ['added', 'updated', 'removed', 'errors', 'unchanged']
            : ['added', 'updated', 'errors', 'unchanged'],
    );

    readonly categoryCounts: ReadonlySignal<Record<UploadCategory, number>> = computed(() => {
        const diff = this.diff.value;
        return {
            added: diff?.added.length ?? 0,
            updated: diff?.updated.length ?? 0,
            removed: diff?.removed.length ?? 0,
            errors: this.#parseResult.value?.errors.length ?? 0,
            unchanged: diff?.unchanged.length ?? 0,
        };
    });

    readonly filteredEntries: ReadonlySignal<UploadFilteredEntries> = computed(() => {
        const query = this.#symbolFilter.value.trim().toLowerCase();
        const matches = (symbol: string | null): boolean =>
            query === '' || (symbol ?? '').toLowerCase().includes(query);
        const diff = this.diff.value;
        return {
            added: (diff?.added ?? []).filter((item) => matches(item.symbol)),
            updated: (diff?.updated ?? []).filter((item) => matches(item.symbol)),
            removed: (diff?.removed ?? []).filter((item) => matches(item.symbol)),
            unchanged: (diff?.unchanged ?? []).filter((item) => matches(item.symbol)),
            errors: (this.#parseResult.value?.errors ?? []).filter((error) => matches(error.symbol)),
        };
    });

    advanceStep(): void {
        if (!this.canAdvance.value) return;
        const step = this.#step.value;
        if (step === 'drop') this.#step.value = 'review';
        else if (step === 'review') this.#step.value = 'confirm';
    }

    retreatStep(): void {
        const step = this.#step.value;
        if (step === 'review') this.#step.value = 'drop';
        else if (step === 'confirm') this.#step.value = 'review';
    }

    setCategoryFilter(category: UploadCategory): void {
        if (this.#categoryFilter.value === category) return;
        this.#categoryFilter.value = category;
        // A new category starts an unfiltered list, mirroring the old
        // reducer transition.
        this.#symbolFilter.value = '';
    }

    setSymbolFilter(query: string): void {
        this.#symbolFilter.value = query;
    }

    setMode(mode: UploadMode): void {
        if (this.#mode.value === mode) return;
        this.#mode.value = mode;
        // The eligible category set depends on the mode ('removed' is
        // merge-ineligible); snap a now-ineligible filter back to the
        // default so the review grid never renders a hidden category.
        if (!this.availableCategories.value.includes(this.#categoryFilter.value)) {
            this.#categoryFilter.value = 'added';
        }
    }

    async selectFile(file: File): Promise<void> {
        if (!this.#deps.canWrite.value) return;
        this.#error.value = null;

        const name = file.name.toLowerCase();
        // CSV-only: the parser splits exclusively on commas, so accepting
        // .tsv or other delimited formats would produce confusing "missing
        // column" errors for files that look correct to the user.
        if (!name.endsWith('.csv')) {
            this.#setFile(null, null);
            this.#error.value = t('admin::pool-management::upload-error-unsupported-format', { fileName: file.name });
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            this.#setFile(null, null);
            this.#error.value = t('admin::pool-management::upload-error-too-large', {
                limit: `${MAX_UPLOAD_BYTES / 1024 / 1024} MB`,
            });
            return;
        }

        try {
            const text = await file.text();
            const parsed = parseInventoryCsv(text);

            // Reject the file entirely when required columns (Symbol, Quantity)
            // are missing — no point in letting the user proceed without them.
            const headerErrors = parsed.errors.filter((e) => e.rowIndex === 0);
            if (headerErrors.length > 0) {
                this.#setFile(null, null);
                this.#error.value = headerErrors.map((e) => e.message).join(' ');
                return;
            }

            this.#setFile({ name: file.name, size: file.size, uploadedAt: Date.now() }, parsed);
            if (parsed.rows.length === 0) {
                this.#error.value = t('admin::pool-management::upload-error-no-valid-rows');
            }
        } catch (e) {
            this.#setFile(null, null);
            this.#error.value = e instanceof Error ? e.message : t('admin::pool-management::upload-error-read-failed');
        }
    }

    clearFile(): void {
        this.#setFile(null, null);
        this.#error.value = null;
    }

    #setFile(file: UploadFileInfo | null, parseResult: InventoryParseResult | null): void {
        this.#file.value = file;
        this.#parseResult.value = parseResult;
        // A new file starts an unfiltered review, mirroring the old
        // reducer transition.
        this.#symbolFilter.value = '';
    }

    /** Progress is monotonic: a late/duplicate event never pulls the bar backward. */
    #advanceProgress(completed: number): void {
        const progress = this.#progress.value;
        if (!progress) return;
        const next = Math.min(Math.max(completed, progress.completed), progress.total);
        if (next === progress.completed) return;
        this.#progress.value = { ...progress, completed: next };
    }

    async apply(): Promise<boolean> {
        if (!this.#deps.canWrite.value) return false;
        if (this.#isApplying.value) return false;
        const diff = this.diff.value;
        const poolId = this.poolId;
        if (!diff) return false;

        // Reject the apply when any added row has no LAB Price — the same
        // count that closes the review step's Continue gate, so the wizard
        // and the apply can't disagree. Kept here as the authoritative
        // backstop since price-less inventory must never reach the backend.
        if (this.missingPriceAddCount.value > 0) {
            this.#error.value = t('admin::pool-management::upload-error-missing-price');
            return false;
        }

        // Only `'replace'` mode actually deletes server rows that aren't
        // in the upload; `'merge'` (the only phase-1 mode) treats those
        // rows as kept. `diff.removed` is still computed so the wizard
        // can surface them to the user if a future mode opts in.
        const mode = this.#mode.value;
        const removedOpsCount = mode === 'replace' ? diff.removed.length : 0;
        const totalOps = diff.added.length + diff.updated.length + removedOpsCount;

        this.#isApplying.value = true;
        this.#error.value = null;
        this.#progress.value = { completed: 0, total: totalOps };
        this.#streamErrors.value = [];

        // Progress = streamed/acked adds + settled update/remove ops. The
        // monotonic clamp caps `completed` at `total`, so over-counting is safe.
        let addsCompleted = 0;
        let opsCompleted = 0;
        const pushProgress = (): void => this.#advanceProgress(addsCompleted + opsCompleted);

        // Subscribe to the live upload-item stream so adds advance the bar
        // as the backend confirms each row. Best-effort: when the socket
        // settings / token aren't available (e.g. unit tests) we fall back
        // to a single jump by the ack's `accepted` count.
        let stream: ReturnType<typeof openInventoryUploadStream> | null = null;
        // Streamed failures are pool-scoped on the wire; only ones whose
        // uploadId matches this apply's POST ack belong to this wizard.
        // Errors can outrun the ack, so they buffer until the id is known.
        let ackUploadId: string | null = null;
        let batchSettledByError = false;
        // The most recent claimed failure; reconciles the done summary so
        // it cannot report rows the backend just said failed to land.
        // Boxed because the writes happen in stream callbacks, which
        // control-flow analysis would otherwise narrow away at the reads.
        const claimedStreamError: { current: InventoryUploadError | null } = { current: null };
        const pendingStreamErrors: InventoryUploadError[] = [];
        const reportStreamError = (error: InventoryUploadError): void => {
            batchSettledByError = true;
            claimedStreamError.current = error;
            this.#streamErrors.value = [
                ...this.#streamErrors.value,
                { reason: error.reason, accepted: error.accepted, failed: error.failed },
            ];
        };
        if (diff.added.length > 0) {
            let streamCtx: { uri: string; token: string | undefined } | null = null;
            try {
                const state = this.#deps.store.getState();
                const settings = getLocatesSocketConnectionSettingsFromState(state);
                if (settings) {
                    let token: string | undefined;
                    try {
                        token = getLocatesAuthPayloadFromState(state);
                    } catch {
                        token = undefined;
                    }
                    streamCtx = { uri: settings.uri, token };
                }
            } catch {
                streamCtx = null;
            }
            if (streamCtx) {
                stream = openInventoryUploadStream({
                    uri: streamCtx.uri,
                    accessToken: streamCtx.token,
                    poolId,
                    // The stream is scoped to this pool's upload room and
                    // dedupes by item id, so every callback is a distinct
                    // added row. Advance the bar as they land, capped at the
                    // number we sent.
                    onItem: () => {
                        if (addsCompleted >= diff.added.length) return;
                        addsCompleted += 1;
                        pushProgress();
                    },
                    // Streamed failures feed the wizard's messages panel
                    // (PLAT-12803). The payload carries no prose — the view
                    // maps the reason to its own copy. Returning true (a
                    // claimed error) also releases the stream's item waiters.
                    onError: (error) => {
                        if (ackUploadId === null) {
                            pendingStreamErrors.push(error);
                            return false;
                        }
                        if (error.uploadId !== ackUploadId) return false;
                        reportStreamError(error);
                        return true;
                    },
                });
            }
        }

        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-upload-in-progress', { count: totalOps }),
        );
        try {
            const ops: Promise<unknown>[] = [];

            // Held separately so the backend's accepted/rejected counts can be
            // read once the batch settles; `ops` only drives the failure check.
            let addBatchPromise: Promise<AddPoolItemsResponse> | null = null;
            if (diff.added.length > 0) {
                const items: NewPoolItem[] = diff.added.map((item) => ({
                    symbol: item.symbol,
                    quantity: item.quantity,
                    // The missing-price guard above already rejected the apply,
                    // so labPrice is guaranteed non-null at this point.
                    price: item.labPrice!,
                }));
                addBatchPromise = addPoolItems({ poolId, request: { items } });
                ops.push(addBatchPromise);
            }
            for (const upd of diff.updated) {
                ops.push(
                    updatePoolItem({
                        poolId,
                        itemId: upd.rowId,
                        request: { quantity: upd.toQuantity, price: upd.toLabPrice },
                    }).finally(() => {
                        opsCompleted += 1;
                        pushProgress();
                    }),
                );
            }
            if (mode === 'replace') {
                for (const rem of diff.removed) {
                    ops.push(
                        removePoolItem({ poolId, itemId: rem.rowId }).finally(() => {
                            opsCompleted += 1;
                            pushProgress();
                        }),
                    );
                }
            }

            const results = await Promise.allSettled(ops);
            const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
            if (failures.length > 0) {
                // Surface any partially-applied rows before reporting the error.
                await this.#refetchServerRows();
                const first = failures[0]!.reason;
                const message = first instanceof Error ? first.message : String(first);
                this.#failApply(message);
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-upload-error', { message }),
                });
                return false;
            }

            // Read the backend's accepted/rejected counts. The batch already
            // settled without rejecting (checked above), so this resolves
            // immediately. `accepted` is the rows that passed validation;
            // `rejected` were dropped up front for an invalid price/quantity.
            // With no adds, fall back to the attempted count (0 rejected).
            const addResult = addBatchPromise ? await addBatchPromise : null;
            const addedCount = addResult ? addResult.accepted : diff.added.length;
            const rejectedCount = addResult ? addResult.rejected : 0;
            if (addResult) {
                // The ack names this apply's uploadId; flush any failures
                // that streamed in before it resolved.
                ackUploadId = addResult.uploadId;
                for (const error of pendingStreamErrors.splice(0)) {
                    if (error.uploadId === ackUploadId) reportStreamError(error);
                }
            }

            // Reconcile the live progress bar to what the backend accepted:
            // with a stream, wait for the streamed items to catch up (bounded
            // by an idle window so a dropped event can't hang the wizard);
            // otherwise jump the bar by `accepted`.
            if (addResult) {
                const expectedAdds = Math.min(addResult.accepted, diff.added.length);
                // Only rely on the stream once it has actually authenticated
                // and joined the room; otherwise a never-ready socket would
                // strand the bar in `waitForItems` until the idle timeout.
                const streamReady = stream ? await stream.whenReady(UPLOAD_STREAM_READY_MS) : false;
                // A failure that already settled the batch means the missing
                // items will never stream — jump the bar instead of waiting.
                if (stream && streamReady && !batchSettledByError) {
                    const waitOutcome = await stream.waitForItems(expectedAdds, UPLOAD_STREAM_IDLE_MS);
                    // Idling out short of the expected count with no claimed
                    // error is exactly the shape of a persistence failure
                    // whose error event hasn't landed yet — hold the
                    // listener open briefly rather than tearing it down
                    // mid-arrival and reporting a false success.
                    if (waitOutcome === 'idle' && !batchSettledByError) {
                        await stream.waitForError(UPLOAD_STREAM_ERROR_GRACE_MS);
                    }
                } else {
                    stream?.[Symbol.dispose]();
                    stream = null;
                    addsCompleted = expectedAdds;
                    pushProgress();
                }
            }

            // Fill the bar to 100% and hold briefly so the apply reads as
            // visually complete before the done screen replaces it. Dispose
            // the stream first so a late item can't tug the bar back down.
            stream?.[Symbol.dispose]();
            stream = null;
            this.#advanceProgress(totalOps);
            await new Promise<void>((resolve) => setTimeout(resolve, UPLOAD_COMPLETE_HOLD_MS));

            // Rows are inserted asynchronously (they arrive over the stream),
            // so refetch now the stream has confirmed them (or it went idle)
            // so the table shows the applied rows without a manual refresh.
            await this.#refetchServerRows();

            // Upload supersedes any pending local adds for the pool.
            this.#deps.onApplySucceeded();

            // A claimed streamed failure overrides the ack's accepted count —
            // the ack only reflects validation, not persistence. A partial
            // failure reports the rows that actually landed; a fatal one
            // (pool-locked / upload-failed) means none did. The update /
            // remove ops settle over HTTP individually, so their counts
            // stand either way.
            const persistedAdds =
                claimedStreamError.current === null
                    ? addedCount
                    : claimedStreamError.current.reason === InventoryUploadErrorReason.ItemsFailed
                      ? Math.min(claimedStreamError.current.accepted, addedCount)
                      : 0;

            // The done transition sets step='done', clears progress, and
            // clears applying/error in one pass. The summary reports what
            // the backend actually accepted; in merge mode `removed` is
            // always 0 because no DELETE was issued.
            this.#isApplying.value = false;
            this.#progress.value = null;
            this.#error.value = null;
            this.#appliedSummary.value = {
                added: persistedAdds,
                updated: diff.updated.length,
                removed: removedOpsCount,
                rejected: rejectedCount,
            };
            this.#step.value = 'done';
            this.#replaceToast(
                toastId,
                claimedStreamError.current !== null
                    ? {
                          variant: 'error',
                          message: t('admin::pool-management::toast-upload-problems'),
                      }
                    : {
                          variant: 'success',
                          message:
                              rejectedCount > 0
                                  ? t('admin::pool-management::toast-upload-success-with-rejected', {
                                        added: persistedAdds,
                                        updated: diff.updated.length,
                                        removed: removedOpsCount,
                                        rejected: rejectedCount,
                                    })
                                  : t('admin::pool-management::toast-upload-success', {
                                        added: persistedAdds,
                                        updated: diff.updated.length,
                                        removed: removedOpsCount,
                                    }),
                      },
            );
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::upload-error-apply-failed');
            this.#failApply(message);
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-upload-error', { message }),
            });
            return false;
        } finally {
            stream?.[Symbol.dispose]();
        }
    }

    downloadTemplate(): void {
        if (typeof document === 'undefined') return;
        const csv = createInventoryTemplateCsv();
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'inventory-template.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Defer revocation: synchronous revoke can race with the browser's
        // download initiation in Firefox and Safari and abort the download.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    #failApply(message: string): void {
        this.#isApplying.value = false;
        this.#progress.value = null;
        this.#error.value = message;
    }

    async #refetchServerRows(): Promise<void> {
        await Promise.all([
            invalidatePoolItemsQuery(this.#deps.queryClient, this.poolId),
            invalidatePoolChangeLogQuery(this.#deps.queryClient, this.poolId),
        ]);
        this.#deps.onServerRowsRefetched();
    }

    #startActionToast(message: string): string {
        const id = crypto.randomUUID();
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: { id, message, group: 'default', variant: 'info', autoHideDuration: null },
        });
        return id;
    }

    #replaceToast(startId: string, finalToast: { message: string; variant: 'success' | 'error' }): void {
        this.#deps.store.dispatch({ type: 'toast::dismiss', id: startId });
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: { message: finalToast.message, group: 'default', variant: finalToast.variant },
        });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
